/**
 * Global Carousel Coordinator
 * Prevents simultaneous DOM updates across multiple carousels
 * Uses intersection observer to pause off-screen carousels for performance
 */

class CarouselCoordinator {
  constructor() {
    this.carousels = new Map();
    this.updateLock = false;
    this.intersectionObserver = null;
    this.initIntersectionObserver();
  }

  /**
   * Initialize intersection observer to pause off-screen carousels
   */
  initIntersectionObserver() {
    // Create observer with threshold for when carousel is mostly visible
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const carouselId = entry.target.dataset.carouselId;
          if (!carouselId) return;

          const carousel = this.carousels.get(carouselId);
          if (!carousel) return;

          // Pause when not visible, resume when visible
          if (entry.isIntersecting) {
            carousel.paused = false;
            if (
              carousel.element &&
              typeof carousel.element.resumeAutoplay === 'function'
            )
              carousel.element.resumeAutoplay();
          } else {
            carousel.paused = true;
            if (
              carousel.element &&
              typeof carousel.element.pauseAutoplay === 'function'
            )
              carousel.element.pauseAutoplay();
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% visible
        rootMargin: '50px', // Start observing slightly before entering viewport
      }
    );
  }

  /**
   * Register a carousel with the coordinator
   * @param {string} carouselId - Unique identifier for the carousel
   * @param {number} interval - Autoplay interval in milliseconds
   * @param {HTMLElement & { pauseAutoplay: () => void, resumeAutoplay: () => void }} element - The carousel container element. Must implement pauseAutoplay() and resumeAutoplay() methods for intersection observer control.
   */
  register(carouselId, interval, element) {
    this.carousels.set(carouselId, {
      interval,
      lastUpdate: Date.now(),
      paused: false,
      element,
    });

    // Add data attribute for intersection observer
    if (element) {
      element.dataset.carouselId = carouselId;
      this.intersectionObserver.observe(element);
    }

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      console.log(
        `[CarouselCoordinator] Registered carousel: ${carouselId} with ${interval}ms interval`
      );
    }
  }

  /**
   * Unregister a carousel (cleanup on removal)
   * @param {string} carouselId - Carousel to unregister
   */
  unregister(carouselId) {
    const carousel = this.carousels.get(carouselId);
    if (carousel && carousel.element) {
      this.intersectionObserver.unobserve(carousel.element);
    }
    this.carousels.delete(carouselId);
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      console.log(`[CarouselCoordinator] Unregistered carousel: ${carouselId}`);
    }
  }

  /**
   * Request a DOM update with lock protection
   * Prevents multiple carousels from updating simultaneously
   * @param {string} carouselId - Carousel requesting update
   * @param {Function} updateFn - Async function that performs the update
   * @returns {Promise<boolean>} - True if update executed, false if skipped
   */
  async requestUpdate(carouselId, updateFn) {
    const carousel = this.carousels.get(carouselId);

    // Skip if carousel is paused
    if (!carousel || carousel.paused) {
      return false;
    }

    // Wait for lock with timeout (prevent infinite wait)
    const startWait = Date.now();
    const maxWait = 250; // 250ms max wait (increased for slower devices)

    while (this.updateLock) {
      if (Date.now() - startWait > maxWait) {
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          console.warn(
            `[CarouselCoordinator] ${carouselId} update timeout - skipping this cycle`
          );
        }
        return false;
      }
      await new Promise((resolve) => setTimeout(resolve, 16)); // Wait one frame
    }

    // Acquire lock
    this.updateLock = true;

    try {
      // Execute the update
      await updateFn();

      // Update last update time
      carousel.lastUpdate = Date.now();

      return true;
    } catch (error) {
      console.error(
        `[CarouselCoordinator] Error during ${carouselId} update:`,
        error
      );
      return false;
    } finally {
      // Always release lock
      this.updateLock = false;
    }
  }

  /**
   * Manually pause a carousel
   * @param {string} carouselId - Carousel to pause
   */
  pause(carouselId) {
    const carousel = this.carousels.get(carouselId);
    if (carousel) {
      carousel.paused = true;
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        console.log(`[CarouselCoordinator] Paused: ${carouselId}`);
      }
    }
  }

  /**
   * Manually resume a carousel
   * @param {string} carouselId - Carousel to resume
   */
  resume(carouselId) {
    const carousel = this.carousels.get(carouselId);
    if (carousel) {
      carousel.paused = false;
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        console.log(`[CarouselCoordinator] Resumed: ${carouselId}`);
      }
    }
  }

  /**
   * Pause all carousels
   */
  pauseAll() {
    this.carousels.forEach((carousel, id) => {
      this.pause(id);
    });
  }

  /**
   * Resume all carousels
   */
  resumeAll() {
    this.carousels.forEach((carousel, id) => {
      this.resume(id);
    });
  }

  /**
   * Get status for debugging
   * @returns {Object} - Status of all carousels
   */
  getStatus() {
    const status = {};
    this.carousels.forEach((carousel, id) => {
      status[id] = {
        interval: carousel.interval,
        paused: carousel.paused,
        lastUpdate: carousel.lastUpdate,
        timeSinceUpdate: Date.now() - carousel.lastUpdate,
      };
    });
    return status;
  }
}

// Initialize global coordinator
window.carouselCoordinator = new CarouselCoordinator();

// Debug helper — only available in theme editor, not exposed to production visitors
if (
  typeof window !== 'undefined' &&
  typeof Shopify !== 'undefined' &&
  Shopify.designMode
) {
  window.debugCarousels = () => {
    console.table(window.carouselCoordinator.getStatus());
  };
}
